import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { boxSizingStyles } from '../../styles/lit-shared';

export type UiButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

@customElement('ui-button')
export class UiButton extends LitElement {
  static styles = [
    boxSizingStyles,
    css`
    :host {
      display: inline-flex;
    }

    :host([fullwidth]) {
      display: flex;
      width: 100%;
    }

    button {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: var(--space-xs);
      width: 100%;
      padding: 0.75rem 1.5rem;
      border: none;
      border-radius: var(--radius-sm);
      font-family: var(--font-sans);
      font-weight: 600;
      font-size: 0.9375rem;
      letter-spacing: 0.01em;
      line-height: 1.4;
      cursor: pointer;
      overflow: hidden;
      transition:
        transform var(--duration-normal) var(--ease-out),
        box-shadow var(--duration-normal) var(--ease-out),
        background var(--duration-normal) var(--ease-out),
        border-color var(--duration-normal) var(--ease-out),
        opacity var(--duration-fast) var(--ease-out);
    }

    /* --- Primary (default) --- */
    button.primary {
      background: var(--gradient-primary);
      color: var(--palette-white);
      box-shadow: var(--shadow-glow-btn);
    }

    button.primary::before {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(
        105deg,
        transparent 40%,
        rgb(255 255 255 / 18%) 50%,
        transparent 60%
      );
      transform: translateX(-100%);
      transition: transform var(--duration-slow) var(--ease-out);
    }

    button.primary:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: var(--shadow-glow-btn-hover);
    }

    button.primary:hover:not(:disabled)::before {
      transform: translateX(100%);
    }

    button.primary:active:not(:disabled) {
      transform: translateY(0);
    }

    /* --- Secondary --- */
    button.secondary {
      background: var(--color-surface);
      color: var(--color-text);
      border: 1px solid var(--color-border-strong);
      box-shadow: var(--shadow-sm);
    }

    button.secondary:hover:not(:disabled) {
      background: var(--color-surface-hover);
      border-color: var(--color-border-glow);
      transform: translateY(-1px);
    }

    /* --- Ghost --- */
    button.ghost {
      background: transparent;
      color: var(--color-primary-light);
      box-shadow: none;
    }

    button.ghost:hover:not(:disabled) {
      background: rgb(139 92 246 / 10%);
    }

    /* --- Danger --- */
    button.danger {
      background: linear-gradient(135deg, #e11d48, #be123c);
      color: var(--palette-white);
      box-shadow: 0 0 20px rgb(225 29 72 / 25%);
    }

    button.danger:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 0 28px rgb(225 29 72 / 35%);
    }

    button:disabled {
      opacity: 0.55;
      cursor: not-allowed;
      transform: none;
    }

    .spinner {
      width: 1rem;
      height: 1rem;
      border: 2px solid rgb(255 255 255 / 30%);
      border-top-color: var(--palette-white);
      border-radius: 50%;
      animation: spin 0.65s linear infinite;
      flex-shrink: 0;
    }

    button.secondary .spinner,
    button.ghost .spinner {
      border-color: rgb(139 92 246 / 25%);
      border-top-color: var(--palette-violet-500);
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `,
  ];

  @property({ type: String }) variant: UiButtonVariant = 'primary';
  @property({ type: String }) type: 'button' | 'submit' | 'reset' = 'button';
  @property({ type: String, attribute: 'form' }) formId = '';
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) loading = false;
  @property({ type: Boolean, reflect: true, attribute: 'fullwidth' }) fullWidth = false;

  private handleClick(e: Event) {
    if (this.disabled || this.loading) return;

    this.dispatchEvent(
      new CustomEvent('ui-click', {
        bubbles: true,
        composed: true,
        detail: { originalEvent: e },
      }),
    );

    if (this.type !== 'submit') return;

    const form = this.closest('form');
    if (form) {
      e.preventDefault();
      form.requestSubmit();
    }
  }

  render() {
    const isDisabled = this.disabled || this.loading;

    return html`
      <button
        class=${this.variant}
        type=${this.type}
        form=${this.formId || undefined}
        ?disabled=${isDisabled}
        aria-busy=${this.loading ? 'true' : 'false'}
        @click=${this.handleClick}
      >
        ${this.loading ? html`<span class="spinner" aria-hidden="true"></span>` : ''}
        <slot></slot>
      </button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ui-button': UiButton;
  }
}
